import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import FileUploader from '../UI/FileUploader';
import { useUIStore } from '@/store/useUIStore';
import {
   initiateCreditPackage,
   submitCreditPackageProof,
   getCreditPackagePaymentDetails,
   type PackageTier,
   type PaymentDetails,
} from '@/api/vendo';

export interface BuyCreditPackageData {
   tier: PackageTier;
   credits: number;
   amount: number;
   pricePerCredit: number;
   label: string; // "Bronze", "Gold", etc — display only
   bonusCredits?: number; // free promo credits granted on top, on approval
   points?: number; // loyalty points the distributor earns on approval
}

interface Props {
   data: BuyCreditPackageData;
}

type SelectedAccount =
   | { kind: 'bank'; index: number }
   | { kind: 'gcash'; index: number }
   | null;

const BuyCreditPackage = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const queryClient = useQueryClient();

   const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
   const [initiating, setInitiating] = useState(true);
   const [initError, setInitError] = useState<string>('');
   const [selected, setSelected] = useState<SelectedAccount>(null);
   const [proofUrl, setProofUrl] = useState<string>('');
   const [uploading, setUploading] = useState(false);

   const bonusCredits = data.bonusCredits ?? 0;
   const totalCredits = data.credits + bonusCredits;

   // On mount, only READ the payment accounts — no purchase row is created.
   // The CreditPurchase is created at submit time (once proof is uploaded), so
   // abandoned checkouts leave no orphan "awaiting payment proof" rows.
   useEffect(() => {
      let cancelled = false;
      (async () => {
         try {
            const details = await getCreditPackagePaymentDetails();
            if (cancelled) return;
            setPaymentDetails(details);
            if (details.bankAccounts.length > 0) {
               setSelected({ kind: 'bank', index: 0 });
            } else if (details.gcashAccounts.length > 0) {
               setSelected({ kind: 'gcash', index: 0 });
            }
         } catch (err: any) {
            if (cancelled) return;
            const msg =
               err?.response?.data?.error ?? 'Failed to load payment details.';
            setInitError(msg);
         } finally {
            if (!cancelled) setInitiating(false);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [data.tier]);

   const submitProofM = useMutation({
      // Create the purchase AND attach proof — only now, at submit. The
      // server reuses any existing pending row for this buyer+tier, so a
      // double-submit can't create duplicates.
      mutationFn: async () => {
         const paymentMethod: 'gcash' | 'bank-transfer' =
            selected?.kind === 'gcash' ? 'gcash' : 'bank-transfer';
         const { purchase } = await initiateCreditPackage(data.tier);
         return submitCreditPackageProof(purchase._id, {
            paymentMethod,
            paymentProofUrl: proofUrl,
         });
      },
      onSuccess: () => {
         queryClient.invalidateQueries({ queryKey: ['vendo', 'my-credit-packages'] });
         toast.success(
            'Payment proof submitted — admin will credit your wallet shortly.',
            { autoClose: 3500 }
         );
         handleClose();
      },
      onError: (err: any) => {
         toast.error(err?.response?.data?.error ?? 'Failed to submit payment proof.');
      },
   });

   const handleSubmit = () => {
      if (uploading) {
         toast.info('Wait for the screenshot to finish uploading.');
         return;
      }
      if (!selected) {
         toast.error('Pick where you sent the payment.');
         return;
      }
      if (!proofUrl) {
         toast.error('Upload a screenshot of your payment.');
         return;
      }
      submitProofM.mutate();
   };

   const totalAccounts = useMemo(() => {
      if (!paymentDetails) return 0;
      return paymentDetails.bankAccounts.length + paymentDetails.gcashAccounts.length;
   }, [paymentDetails]);

   return (
      <Wrapper>
         <Summary>
            <SummaryHead>
               <SummaryLabel>Order summary</SummaryLabel>
               <TierBadge>{data.label}</TierBadge>
            </SummaryHead>
            <SummaryRow>
               <SumLbl>{data.credits.toLocaleString()} credits</SumLbl>
               <SumVal>₱{data.amount.toLocaleString()}</SumVal>
            </SummaryRow>
            {bonusCredits > 0 && (
               <BonusRow>
                  <span>🔥 +{bonusCredits.toLocaleString()} bonus credits</span>
                  <strong>{totalCredits.toLocaleString()} credits total</strong>
               </BonusRow>
            )}
            {!!data.points && data.points > 0 && (
               <PerkRow>✨ earn {data.points.toLocaleString()} points on approval</PerkRow>
            )}
            <SummaryHint>
               ₱{data.pricePerCredit}/credit · 1 credit gives 1 vendo device a lifetime license.
               {bonusCredits > 0 && ' Bonus credits are added when admin approves.'}
            </SummaryHint>
         </Summary>

         {initiating && (
            <SkeletonLines lines={3} />
         )}

         {!!initError && !initiating && (
            <ErrorBox>{initError}</ErrorBox>
         )}

         {!initiating && !initError && paymentDetails && (
            <>
               <SectionTitle>1. Send payment to one of these accounts</SectionTitle>
               {totalAccounts === 0 ? (
                  <ErrorBox>
                     SmartFi hasn't configured payment accounts yet. Contact support
                     before sending any money.
                  </ErrorBox>
               ) : (
                  <AccountList>
                     {paymentDetails.bankAccounts.map((b, idx) => {
                        const isSelected =
                           selected?.kind === 'bank' && selected.index === idx;
                        return (
                           <AccountOption
                              key={`bank-${idx}`}
                              $selected={isSelected}
                              onClick={() => setSelected({ kind: 'bank', index: idx })}
                           >
                              <AcctType>🏦 Bank</AcctType>
                              <AcctBody>
                                 <AcctName>{b.bankName}</AcctName>
                                 <AcctMeta>
                                    <Mono>{b.accountNumber}</Mono> · {b.accountName}
                                 </AcctMeta>
                              </AcctBody>
                              <AcctRadio $selected={isSelected} />
                           </AccountOption>
                        );
                     })}
                     {paymentDetails.gcashAccounts.map((g, idx) => {
                        const isSelected =
                           selected?.kind === 'gcash' && selected.index === idx;
                        return (
                           <AccountOption
                              key={`gcash-${idx}`}
                              $selected={isSelected}
                              onClick={() => setSelected({ kind: 'gcash', index: idx })}
                           >
                              <AcctType>📱 GCash</AcctType>
                              <AcctBody>
                                 <AcctName>{g.name}</AcctName>
                                 <AcctMeta>
                                    <Mono>{g.number}</Mono>
                                 </AcctMeta>
                              </AcctBody>
                              <AcctRadio $selected={isSelected} />
                           </AccountOption>
                        );
                     })}
                  </AccountList>
               )}
               {paymentDetails.paymentInstructions && (
                  <PaymentInstructions>
                     {paymentDetails.paymentInstructions}
                  </PaymentInstructions>
               )}

               <SectionTitle style={{ marginTop: 8 }}>2. Upload your payment screenshot</SectionTitle>
               <Field>
                  <Hint>
                     A photo or screenshot of your payment confirmation. Admin uses this to
                     verify before crediting your wallet.
                  </Hint>
                  <FileUploader
                     value={proofUrl}
                     onChange={setProofUrl}
                     onUploadingChange={setUploading}
                  />
               </Field>

               <Disclaimer>
                  Your wallet will be credited <strong>after admin verifies</strong> the
                  payment. SmartFi never auto-credits — this is the manual review step.
               </Disclaimer>
            </>
         )}

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={handleSubmit}>
               {(() => {
                  // Submit is blocked until a screenshot is uploaded (and an
                  // account picked) — and no purchase row is created until then.
                  const blocked =
                     initiating ||
                     !!initError ||
                     submitProofM.isPending ||
                     uploading ||
                     !selected ||
                     !proofUrl;
                  return (
                     <Btn
                        title={
                           submitProofM.isPending
                              ? 'Submitting…'
                              : uploading
                                ? 'Uploading…'
                                : !proofUrl
                                  ? 'Upload payment screenshot first'
                                  : `Submit payment proof · ₱${data.amount.toLocaleString()}`
                        }
                        type={blocked ? 'disabled' : 'normal'}
                        size="small"
                        bgcolor={blocked ? colors.GRAY10 : colors.PRIMARY}
                        textcolor={blocked ? colors.GRAY1 : colors.WHITE}
                     />
                  );
               })()}
            </div>
         </BtnRow>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 520px;
   max-width: 100%;
   max-height: 75vh;
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 12px;
   @media (max-width: 768px) { width: 100%; max-height: 80vh; }
`;

const Summary = styled.div`
   padding: 12px 14px;
   border-radius: 10px;
   background: var(--app-brand-tint);
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const SummaryHead = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
`;

const SummaryLabel = styled.span`
   font-size: 10px;
   font-weight: 800;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;

const TierBadge = styled.span`
   font-size: 10px;
   font-weight: 800;
   padding: 2px 8px;
   background: ${colors.PRIMARY};
   color: white;
   border-radius: 8px;
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;

const SummaryRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 12px;
`;

const SumLbl = styled.span`
   font-size: 16px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const SumVal = styled.span`
   font-size: 22px;
   font-weight: 900;
   color: ${colors.PRIMARY};
`;

const BonusRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 12px;
   margin-top: 4px;
   font-size: 13px;
   font-weight: 700;
   color: ${colors.SUCCESS};
`;

const PerkRow = styled.div`
   margin-top: 2px;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.PRIMARY};
`;

const SummaryHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const SectionTitle = styled.h4`
   font-size: 12px;
   font-weight: 800;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;


const ErrorBox = styled.div`
   padding: 12px 14px;
   border-radius: 8px;
   background: ${colors.DANGER}10;
   border: 1px solid ${colors.DANGER}30;
   color: ${colors.DANGER};
   font-size: 12px;
   line-height: 1.5;
`;

const AccountList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const AccountOption = styled.button<{ $selected: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 12px;
   border-radius: 8px;
   border: 2px solid ${({ $selected }) => ($selected ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $selected }) =>
      $selected ? `${colors.PRIMARY}08` : 'var(--app-surface)'};
   cursor: pointer;
   text-align: left;
   font-family: inherit;
   transition: all 0.15s;
   &:hover { border-color: ${colors.PRIMARY}; }
`;

const AcctType = styled.span`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   width: 60px;
   flex-shrink: 0;
`;

const AcctBody = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;

const AcctName = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const AcctMeta = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const Mono = styled.span`
   font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
`;

const AcctRadio = styled.span<{ $selected: boolean }>`
   width: 18px;
   height: 18px;
   border-radius: 50%;
   border: 2px solid ${({ $selected }) => ($selected ? colors.PRIMARY : colors.GRAY10)};
   background: ${({ $selected }) => ($selected ? colors.PRIMARY : 'transparent')};
   box-shadow: ${({ $selected }) =>
      $selected ? `inset 0 0 0 3px white` : 'none'};
   flex-shrink: 0;
`;

const PaymentInstructions = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   padding: 8px 10px;
   background: ${colors.GRAY14};
   border-radius: 6px;
   line-height: 1.5;
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const Lbl = styled.label`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const Hint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const TextInput = styled.input`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const Disclaimer = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   padding: 8px 10px;
   background: ${colors.PRIMARY}08;
   border-left: 3px solid ${colors.PRIMARY};
   border-radius: 4px;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

export default BuyCreditPackage;
